import { useState } from 'react';
import { Pane, Tablist, Tab, Dialog, Button, Text, toaster } from 'evergreen-ui';
import TurniDayDetailModal from './Turni/TurniDayDetailModal';
import TurniShiftModal from './Turni/TurniShiftModal';
import TurniOrarioFissoModal from './Turni/TurniOrarioFissoModal';
import TurniFerieRequestModal from './Turni/TurniFerieRequestModal';
import TurniManagerFerieModal from './Turni/TurniManagerFerieModal';
import TurniHeader from './Turni/TurniHeader';
import TurniRangesList from './Turni/TurniRangesList';
import TurniSidebar from './Turni/TurniSidebar';
import VistaCompatta from './Turni/Views/VistaCompatta';
import VistaMensile from './Turni/Views/VistaMensile';
import VistaSettimanale from './Turni/Views/VistaSettimanale';
import useWindowSize from '../utils/useWindowSize';
import useTurni, { EMPTY_RANGE } from './Turni/useTurni';
import useFerie from './Turni/useFerie';
import useTurniCalendar from './Turni/useTurniCalendar';
import { setActiveNightIntervals } from '../services/turniService';

export default function TurniOperatori({ isDirector, isEducator, isOperator }) {
  const isMobile = useWindowSize() < 768;

  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [isTurnoModalOpen, setIsTurnoModalOpen] = useState(false);
  const [isFerieModalOpen, setIsFerieModalOpen] = useState(false);
  const [isManagerFerieOpen, setIsManagerFerieOpen] = useState(false);
  const [isOrarioFissoOpen, setIsOrarioFissoOpen] = useState(false);
  const [showRanges, setShowRanges] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [showOnlyUncovered, setShowOnlyUncovered] = useState(false);
  const [viewMode, setViewMode] = useState('mensile');
  const [isDragOverTrash, setIsDragOverTrash] = useState(false);
  const [isDropTypeOpen, setIsDropTypeOpen] = useState(false);
  const [pendingDropData, setPendingDropData] = useState({ name: '', date: '' });

  const {
    userName, selectedMonth, setSelectedMonth,
    turniData, collaboratorsList,
    shiftInputs, setShiftInputs,
    loadData, handleSaveTurno, handleDeleteTurno, handleTimbra,
    handleDragStart, handleDrop,
    shiftRanges, rangeInputs, setRangeInputs,
    handleSaveShiftRange, handleDeleteShiftRange,
  } = useTurni({ isDirector, isEducator });

  const {
    managerFerieList, ferieInputs, setFerieInputs,
    handleRequestFerie, handleOpenManagerFerie, handleOpenMyFerie, handleUpdateFerieStatus,
  } = useFerie({ isDirector, userName, onFerieApproved: loadData });

  const {
    mockDays, getInitials, getBadgeProps, getStatusColor,
    coveragePercentage, coveredMonthHours, expectedMonthHours,
  } = useTurniCalendar({ turniData, collaboratorsList, managerFerieList, selectedMonth });

  const handleDayClick = (day) => { setSelectedDay(day); setIsDayModalOpen(true); };

  const handleSetActiveNightIntervals = async (shiftId, intervalli) => {
    try {
      await setActiveNightIntervals(shiftId, intervalli);
      await loadData();
    } catch (e) {
      toaster.danger('Errore salvataggio emergenza: ' + e.message);
    }
  };

  const onDrop = (e, dateStr) => {
    const droppedName = handleDrop(e, dateStr);
    if (droppedName) {
      setPendingDropData({ name: droppedName, date: dateStr });
      setIsDropTypeOpen(true);
    }
  };

  const handleDropAsTurno = () => {
    setIsDropTypeOpen(false);
    setIsTurnoModalOpen(true);
  };

  const handleDropAsFerie = () => {
    setIsDropTypeOpen(false);
    setFerieInputs(prev => ({ ...prev, operator_name: pendingDropData.name, start_date: pendingDropData.date, end_date: pendingDropData.date }));
    setIsFerieModalOpen(true);
  };

  const handleShiftDragStart = (e, shiftId) => e.dataTransfer.setData('shiftId', String(shiftId));
  const handleLeaveDragStart = (e, leaveId) => e.dataTransfer.setData('leaveId', String(leaveId));

  const handleTrashDrop = async (e) => {
    e.preventDefault();
    setIsDragOverTrash(false);
    const shiftId = e.dataTransfer.getData('shiftId');
    const leaveId = e.dataTransfer.getData('leaveId');
    if (shiftId) {
      try { await handleDeleteTurno(Number(shiftId)); } catch (_e) {}
    } else if (leaveId) {
      try { await handleUpdateFerieStatus(Number(leaveId), 'rifiutata'); } catch (_e) {}
    }
  };

  const openEditRange = (r) => {
    setRangeInputs({
      batch_id: r.batch_id,
      operator_name: r.operator_name,
      from_date: r.from_date,
      to_date: r.to_date,
      start_time: r.start_time ? r.start_time.substring(0, 5) : '',
      end_time: r.end_time ? r.end_time.substring(0, 5) : '',
      shift_type: r.shift_type || 'turno',
      is_overtime: !!Number(r.is_overtime),
      shift_period: r.shift_period || '',
      notes: r.notes || '',
    });
    setIsOrarioFissoOpen(true);
  };

  return (
    <Pane padding={24} maxWidth={1600} marginX="auto" width="100%">

      <TurniHeader
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        coveragePercentage={coveragePercentage}
        coveredMonthHours={coveredMonthHours}
        expectedMonthHours={expectedMonthHours}
        showOnlyUncovered={showOnlyUncovered}
        setShowOnlyUncovered={setShowOnlyUncovered}
        isDirector={isDirector}
        isEducator={isEducator}
        isOperator={isOperator}
        onInsert={() => setIsTurnoModalOpen(true)}
        onOrarioFisso={() => { setRangeInputs(EMPTY_RANGE); setIsOrarioFissoOpen(true); }}
        onFerie={() => setIsFerieModalOpen(true)}
        onMieFerie={async () => { await handleOpenMyFerie(); setIsManagerFerieOpen(true); }}
        onGestioneFerie={async () => { await handleOpenManagerFerie(); setIsManagerFerieOpen(true); }}
      />

      {isDirector && (
        <TurniRangesList
          shiftRanges={shiftRanges}
          showRanges={showRanges}
          setShowRanges={setShowRanges}
          onEdit={openEditRange}
          onDelete={handleDeleteShiftRange}
        />
      )}

      <Pane display="flex" gap={24} flexDirection={isMobile ? 'column-reverse' : 'row'}>
        <Pane width={isMobile ? '100%' : 250} marginTop={isMobile ? 16 : 0}>
          <TurniSidebar
            collaboratorsList={collaboratorsList}
            getInitials={getInitials}
            isDirector={isDirector}
            viewMode={viewMode}
            isDragOverTrash={isDragOverTrash}
            setIsDragOverTrash={setIsDragOverTrash}
            onTrashDrop={handleTrashDrop}
            onDragStart={handleDragStart}
          />
        </Pane>

        <Pane flex={1} background="white" padding={16} borderRadius={8} border="default" overflowX="auto">
          <Tablist marginBottom={16}>
            <Tab isSelected={viewMode === 'compatta'} onSelect={() => setViewMode('compatta')}>Vista compatta</Tab>
            <Tab isSelected={viewMode === 'mensile'} onSelect={() => setViewMode('mensile')}>Vista mensile</Tab>
            {isDirector && <Tab isSelected={viewMode === 'settimanale'} onSelect={() => setViewMode('settimanale')}>Vista settimanale</Tab>}
          </Tablist>

          {viewMode === 'compatta' && (
            <VistaCompatta
              mockDays={mockDays}
              showOnlyUncovered={showOnlyUncovered}
              handleDayClick={handleDayClick}
              getStatusColor={getStatusColor}
            />
          )}

          {viewMode === 'mensile' && (
            <VistaMensile
              mockDays={mockDays}
              showOnlyUncovered={showOnlyUncovered}
              turniData={turniData}
              managerFerieList={managerFerieList}
              isDirector={isDirector}
              getInitials={getInitials}
              getBadgeProps={getBadgeProps}
              handleDrop={onDrop}
              handleDayClick={handleDayClick}
              setShiftInputs={setShiftInputs}
              shiftInputs={shiftInputs}
              setIsTurnoModalOpen={setIsTurnoModalOpen}
              onShiftDragStart={handleShiftDragStart}
              onLeaveDragStart={handleLeaveDragStart}
            />
          )}

          {viewMode === 'settimanale' && isDirector && (
            <VistaSettimanale
              mockDays={mockDays}
              selectedWeek={selectedWeek}
              setSelectedWeek={setSelectedWeek}
              collaboratorsList={collaboratorsList}
              turniData={turniData}
              managerFerieList={managerFerieList}
              isDirector={isDirector}
              getInitials={getInitials}
              getBadgeProps={getBadgeProps}
              handleTimbra={handleTimbra}
            />
          )}
        </Pane>
      </Pane>

      <Dialog
        isShown={isDropTypeOpen}
        title="Cosa vuoi inserire?"
        onCloseComplete={() => setIsDropTypeOpen(false)}
        hasFooter={false}
        width={360}
      >
        <Text size={400} color="muted" display="block" marginBottom={16}>
          Collaboratore: <strong>{pendingDropData.name}</strong> — {pendingDropData.date}
        </Text>
        <Pane display="flex" flexDirection="column" gap={10} paddingBottom={16}>
          <Button height={44} width="100%" appearance="primary" onClick={handleDropAsTurno}>
            Turno
          </Button>
          <Button height={44} width="100%" onClick={handleDropAsFerie}>
            Ferie / Permesso / Malattia
          </Button>
        </Pane>
      </Dialog>

      <TurniDayDetailModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        selectedDay={selectedDay}
        isDirector={isDirector}
        onRemoveShift={async (id) => {
          try { await handleDeleteTurno(id); setIsDayModalOpen(false); } catch (_e) {}
        }}
        onAssignClick={(dateStr) => {
          setIsDayModalOpen(false);
          if (isDirector) {
            setShiftInputs(prev => ({ ...prev, date: dateStr }));
            setIsTurnoModalOpen(true);
          }
        }}
        onSetActiveNightIntervals={handleSetActiveNightIntervals}
      />

      <TurniShiftModal
        isOpen={isTurnoModalOpen}
        onClose={() => setIsTurnoModalOpen(false)}
        onSave={async () => { const ok = await handleSaveTurno(); if (ok) setIsTurnoModalOpen(false); }}
        isDirector={isDirector}
        shiftInputs={shiftInputs}
        setShiftInputs={setShiftInputs}
        userName={userName}
        collaboratorsList={collaboratorsList}
        turniData={turniData}
      />

      <TurniOrarioFissoModal
        isOpen={isOrarioFissoOpen}
        onClose={() => setIsOrarioFissoOpen(false)}
        onSave={async () => { const ok = await handleSaveShiftRange(); if (ok) setIsOrarioFissoOpen(false); }}
        rangeInputs={rangeInputs}
        setRangeInputs={setRangeInputs}
        userName={userName}
        collaboratorsList={collaboratorsList}
      />

      <TurniFerieRequestModal
        isOpen={isFerieModalOpen}
        onClose={() => setIsFerieModalOpen(false)}
        onRequest={async () => { const ok = await handleRequestFerie(); if (ok) setIsFerieModalOpen(false); }}
        ferieInputs={ferieInputs}
        setFerieInputs={setFerieInputs}
        isDirector={isDirector}
        collaboratorsList={collaboratorsList}
        selfName={userName}
      />

      <TurniManagerFerieModal
        isOpen={isManagerFerieOpen}
        onClose={() => setIsManagerFerieOpen(false)}
        managerFerieList={managerFerieList}
        onUpdateStatus={handleUpdateFerieStatus}
        isDirector={isDirector}
      />
    </Pane>
  );
}
